import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useQuery } from 'react-fetching-library';

// GET a single account by uuid. Mirrors the inline account GET used by
// AccountEdit so the request shape and path stay identical.
const fetchAccountAction = ({ uuid }) => ({
  method: 'GET',
  endpoint: `/accounts/${uuid}`,
});

/**
 * Resolve the display name of the account the current page is scoped to.
 *
 * DS users reach the upload screens for a CHILD account via
 * /:currentAccountUuid/accounts/:accountUuid/... — the child's name is not
 * in metadata, so we fetch it by uuid. When there is no :accountUuid param
 * the page is scoped to the user's own current account, whose name is
 * already in metadata, so no request is made.
 *
 * The name used to be smuggled through the URL as a :title path segment,
 * which broke for accounts whose name contains URL-significant characters
 * (e.g. "test #2" — the # truncated the path and the route failed to
 * match). Sourcing the name here keeps it out of the URL entirely.
 *
 * Returns `{ name, loading }`. `loading` lets the page show a title
 * skeleton while the child-account fetch is in flight; it is always
 * false for the current account, whose name resolves synchronously.
 */
export const useAccountName = () => {
  const { accountUuid } = useParams();
  const currentAccountName = useSelector(
    state => state.user?.metadata?.currentAccount?.businessName
  );

  const action = useMemo(() => fetchAccountAction({ uuid: accountUuid }), [accountUuid]);
  // initFetch only when there is a child account uuid to resolve.
  const { payload, loading, error } = useQuery(action, Boolean(accountUuid));

  if (!accountUuid) return { name: currentAccountName || '', loading: false };

  const name = payload?.businessName || '';
  // Keep loading until the name arrives or the request fails, so there is
  // no empty-title flicker between mount and the first response.
  return { name, loading: loading || (!name && !error) };
};

export default useAccountName;
